import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FaBook, FaChartLine, FaLungs, FaFilePdf, FaExternalLinkAlt, FaWind, FaHeartbeat, FaUniversity } from 'react-icons/fa';
import AnimateOnScroll from '../components/AnimateOnScroll';
import './Educacional.css';

const Educacional = () => {
  const conteudos = [
    {
      id: 1,
      icon: <FaLungs />,
      titulo: "Manovacuometria",
      subtitulo: "Avaliação da Força Muscular Respiratória",
      descricao: "A manovacuometria é o método padrão-ouro para mensurar a força dos músculos respiratórios através da Pressão Inspiratória Máxima (PImáx) e Pressão Expiratória Máxima (PEmáx).",
      topicos: [
        "PImáx: > 60 cmH₂O (normal), 40-60 cmH₂O (fraqueza leve), < 40 cmH₂O (fraqueza significativa)",
        "PEmáx: ≥ 55 cmH₂O associado a melhor sucesso na extubação",
        "Uso de clipe nasal e bocal com orifício de 2mm",
        "Mínimo de 3 manobras aceitáveis, com variação < 10%"
      ],
      referencia: "Gov.br - POP Avaliação da Força Muscular Respiratória",
      link: "https://www.gov.br/ebserh/pt-br/hospitais-universitarios/regiao-centro-oeste/hu-ufgd/acesso-a-informacao/pops-protocolos-e-processos/gerencia-de-atencao-a-saude-gas/unidade-de-clinica-medica-ucm/pop-ucm-004-portaria-56-avaliacao-da-forca-muscular-respiratoria"
    },
    {
      id: 2,
      icon: <FaWind />,
      titulo: "Peak Flow (Pico de Fluxo)",
      subtitulo: "Monitoramento da Função Pulmonar",
      descricao: "O Pico de Fluxo Expiratório (PFE) mede a velocidade máxima do fluxo de ar durante uma expiração forçada, essencial para monitorar obstrução das vias aéreas e avaliar resposta a broncodilatadores.",
      topicos: [
        "Inspiração profunda até CPT seguida de expiração forçada máxima",
        "Valores dependem de idade, sexo e altura do paciente",
        "Uso de clipe nasal e posição preferencialmente sentada",
        "Monitoramento diário na asma para reconhecer início de crises",
        "Equações de referência: EU/EN13826 (Nunn e Gregg)"
      ],
      referencia: "Gov.br - Medidor de Pico de Fluxo Expiratório",
      link: "https://linhasdecuidado.saude.gov.br/portal/asma/medidor-de-pico-fluxo-expiratorio/"
    },
    {
      id: 3,
      icon: <FaHeartbeat />,
      titulo: "Espirometria",
      subtitulo: "Avaliação da Função Pulmonar",
      descricao: "Exame fundamental para diagnóstico e monitoramento de doenças respiratórias, medindo volumes e capacidades pulmonares através de CVF, VEF1 e relação VEF1/CVF.",
      topicos: [
        "CVF (Capacidade Vital Forçada): volume máximo exalado com esforço máximo",
        "VEF1 (Volume Expiratório Forçado 1s): principal indicador de obstrução",
        "Obstrução: VEF1/CVF < 0,70 pós-broncodilatador (DPOC)",
        "Repetibilidade: diferença ≤ 150ml entre maiores valores de CVF e VEF1",
        "Protocolo SBPT 2024: até 8 tentativas para aceitabilidade"
      ],
      referencia: "SBPT - Recomendações de Espirometria 2024",
      link: "https://www.jornaldepneumologia.com.br/details/4061/pt-BR/novas-recomendacoes-de-espirometria-da-sociedade-brasileira-de-pneumologia-e-tisiologia-%E2%80%93-atualizacao-2024"
    },
    {
      id: 4,
      icon: <FaChartLine />,
      titulo: "Técnicas de Mensuração",
      subtitulo: "Procedimentos e Protocolos",
      descricao: "Métodos validados para avaliação precisa da função muscular respiratória em diferentes contextos clínicos.",
      topicos: [
        "PImáx: Mensuração a partir do Volume Residual (VR)",
        "PEmáx: Mensuração a partir da Capacidade Pulmonar Total (CPT)",
        "Pressão sustentada por pelo menos 1 segundo",
        "Métodos alternativos: sniff test, ultrassonografia diafragmática"
      ],
      referencia: "MSD Manuals - Exames de Função Muscular Respiratória",
      link: "https://www.msdmanuals.com/pt/profissional/dist%C3%BArbios-pulmonares/exames-de-fun%C3%A7%C3%A3o-pulmonar/exames-de-fun%C3%A7%C3%A3o-muscular-respirat%C3%B3ria"
    },
    {
      id: 5,
      icon: <FaBook />,
      titulo: "Aplicações Clínicas",
      subtitulo: "Desmame Ventilatório e Reabilitação",
      descricao: "A avaliação da função respiratória é fundamental no processo de desmame da ventilação mecânica e na reabilitação de pacientes com doenças cardiorespiratórias crônicas.",
      topicos: [
        "Preditor eficaz para sucesso da extubação",
        "Redução de taxas de reintubação e mortalidade",
        "Monitoramento em pacientes com DPOC, asma e Parkinson",
        "Avaliação da eficácia do treinamento muscular respiratório",
        "Declínio fisiológico: VEF1 cai 28ml/ano (homens) e 21ml/ano (mulheres) após 35 anos"
      ],
      referencia: "SciELO - Análise da Força Muscular Respiratória",
      link: "https://www.scielo.br/j/fp/a/kZK8GpMG8BYmPkCDdNP4J4S/?lang=pt"
    },
    {
      id: 6,
      icon: <FaFilePdf />,
      titulo: "Treinamento Muscular Inspiratório",
      subtitulo: "Evidências e Protocolos",
      descricao: "O treinamento muscular inspiratório (TMI) demonstra eficácia na melhora da força muscular respiratória e facilitação do desmame ventilatório.",
      topicos: [
        "Uso de dispositivos threshold ou PowerBreathe",
        "Protocolo: 30% a 50% da PImáx inicial",
        "Sessões de 15-30 minutos, 1-2x ao dia",
        "Progressão gradual da carga conforme tolerância"
      ],
      referencia: "Repositório Animaeducação - TMI em VM",
      link: "https://repositorio.animaeducacao.com.br/items/0da20e50-573f-44ea-8c01-ccfcb864a776"
    }
  ];

  return (
    <div className="educacional-page">
      <AnimateOnScroll as="section" className="educacional-hero" variant="fadeUp">
        <Container>
          <h1>Fisioterapia Cardiorespiratória</h1>
          <p>Avaliação, Monitoramento e Treinamento Respiratório</p>
          <p className="hero-subtitle">Materiais, artigos e recursos para profissionais e estudantes com foco em manovacuometria, espirometria, peak flow e reabilitação cardiorespiratória.</p>
        </Container>
      </AnimateOnScroll>

      <Container className="educacional-content">

        <AnimateOnScroll variant="fadeUp" delay={100}>
        <div className="gimm-card-wrapper">
          <Card 
            className="gimm-card" 
            as="a" 
            href="https://gimm_unipampa.gitlab.io/page/" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <div className="gimm-card-icon">
              <FaUniversity />
            </div>
            <Card.Body>
              <h3 className="gimm-card-title">GIMM — Grupo de Informática Médica e Microeletrônica</h3>
              <p className="gimm-card-subtitle">Universidade Federal do Pampa (Unipampa)</p>
              <p className="gimm-card-description">
                O GIMM é o grupo de pesquisa onde nasceu a ideia de desenvolver soluções para a área da saúde. 
                Do curso de Engenharia de Computação da Unipampa (Bagé), o grupo atua no estudo e desenvolvimento 
                de soluções computacionais aplicáveis na saúde e microeletrônica, incluindo sistemas embarcados 
                e aplicações em fisioterapia respiratória. Projetos como FisioLung e Espiro Game fazem parte do portfólio do grupo.
              </p>
              <span className="gimm-card-link">
                <FaExternalLinkAlt /> Conhecer o GIMM
              </span>
            </Card.Body>
          </Card>
        </div>
        </AnimateOnScroll>

        <AnimateOnScroll variant="fadeUp" delay={150}>
        <Row className="content-grid">
          {conteudos.map((item) => (
            <Col key={item.id} xs={12} lg={6} className="content-col">
              <Card className="content-card">
                <div className="card-icon">
                  {item.icon}
                </div>
                <Card.Body>
                  <h3 className="card-title">{item.titulo}</h3>
                  <p className="card-subtitle">{item.subtitulo}</p>
                  <p className="card-description">{item.descricao}</p>
                  
                  <ul className="topicos-list">
                    {item.topicos.map((topico, index) => (
                      <li key={index}>{topico}</li>
                    ))}
                  </ul>

                  <div className="card-footer">
                    <a 
                      href={item.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="referencia"
                    >
                      <FaExternalLinkAlt />
                      {item.referencia}
                    </a>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        </AnimateOnScroll>

        <AnimateOnScroll variant="fadeUp" delay={100}>
        <div className="referencias-section">
          <h3>Referências Bibliográficas</h3>
          <ul className="referencias-list">
            <li>
              <a href="https://www.jornaldepneumologia.com.br/details/4061/pt-BR/novas-recomendacoes-de-espirometria-da-sociedade-brasileira-de-pneumologia-e-tisiologia-%E2%80%93-atualizacao-2024" target="_blank" rel="noopener noreferrer">
                SBPT - Novas recomendações de espirometria da Sociedade Brasileira de Pneumologia e Tisiologia - Atualização 2024
              </a>
            </li>
            <li>
              <a href="https://linhasdecuidado.saude.gov.br/portal/asma/medidor-de-pico-fluxo-expiratorio/" target="_blank" rel="noopener noreferrer">
                Portal Gov.br - Medidor de Pico de Fluxo Expiratório (PFE) - Peak Flow
              </a>
            </li>
            <li>
              <a href="https://www.gov.br/ebserh/pt-br/hospitais-universitarios/regiao-centro-oeste/hu-ufgd/acesso-a-informacao/pops-protocolos-e-processos/gerencia-de-atencao-a-saude-gas/unidade-de-clinica-medica-ucm/pop-ucm-004-portaria-56-avaliacao-da-forca-muscular-respiratoria" target="_blank" rel="noopener noreferrer">
                Portal Gov.br - POP.UCM.004: Avaliação da Força Muscular Respiratória (2024)
              </a>
            </li>
            <li>
              <a href="https://www.msdmanuals.com/pt/profissional/dist%C3%BArbios-pulmonares/exames-de-fun%C3%A7%C3%A3o-pulmonar/exames-de-fun%C3%A7%C3%A3o-muscular-respirat%C3%B3ria" target="_blank" rel="noopener noreferrer">
                MSD Manuals - Exames de Função Muscular Respiratória - Edição para Profissionais
              </a>
            </li>
            <li>
              <a href="https://www.msdmanuals.com/pt/profissional/dist%C3%BArbios-pulmonares/exames-de-fun%C3%A7%C3%A3o-pulmonar/fluxo-de-ar-volumes-pulmonares-e-curvas-fluxo-volume" target="_blank" rel="noopener noreferrer">
                MSD Manuals - Fluxo de ar, volumes pulmonares e curvas fluxo-volume
              </a>
            </li>
            <li>
              <a href="https://www.scielo.br/j/fp/a/kZK8GpMG8BYmPkCDdNP4J4S/?lang=pt" target="_blank" rel="noopener noreferrer">
                SciELO - Análise da força muscular expiratória e respiração espontânea de indivíduos em ventilação mecânica
              </a>
            </li>
            <li>
              <a href="https://www.scielo.br/j/rbfis/a/mKq5MT7FZj4Y99NCrLyg4Rp/?lang=pt" target="_blank" rel="noopener noreferrer">
                SciELO - Avaliação da pressão inspiratória máxima em pacientes críticos não-cooperativos
              </a>
            </li>
            <li>
              <a href="https://sanarmed.com/resumo-de-espirometria-definicao-indicacoes-tecnica-e-analise-do-resultados/" target="_blank" rel="noopener noreferrer">
                Sanar Med - Espirometria: definição, indicações, técnica e análise de resultados
              </a>
            </li>
            <li>
              <a href="https://repositorio.animaeducacao.com.br/items/0da20e50-573f-44ea-8c01-ccfcb864a776" target="_blank" rel="noopener noreferrer">
                Repositório Animaeducação - Treinamento muscular inspiratório em pacientes submetidos à ventilação mecânica
              </a>
            </li>
            <li>
              <a href="https://acervomais.com.br/index.php/saude/article/view/19563" target="_blank" rel="noopener noreferrer">
                Acervo+ - Avaliação de Marcha e Força Muscular Respiratória em Pacientes com Doença de Parkinson
              </a>
            </li>
          </ul>
        </div>
        </AnimateOnScroll>

        <AnimateOnScroll variant="fadeIn" delay={150}>
        <div className="disclaimer">
          <p><strong>Nota:</strong> Os conteúdos apresentados têm caráter informativo e educacional. Para aplicação clínica, consulte sempre as diretrizes atualizadas e protocolos institucionais.</p>
        </div>
        </AnimateOnScroll>
      </Container>
    </div>
  );
};

export default Educacional;
